/* eslint-disable react/react-in-jsx-scope */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from "react";
import ContainerWithSidebar from "@/components/Sidebar";
import {
  allExtensionsString,
  imageFileTypes,
  profileSidebarMenuList,
} from "@/config";
import {
  Card,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  ListItemText,
  Typography,
  Container,
  Stack,
  Button,
  useTheme,
  Box,
} from "@mui/material";
import { inject, observer } from "mobx-react";
import Store from "@/store";
import CommonStore from "@/store/common";
import { UserStore } from "@/store/user";
import { MuiFileInput } from "mui-file-input";
import { enqueueSnackbar } from "notistack";
import { Delete, VerifiedRounded } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { autorun } from "mobx";

const ProfileVerificationPage = inject(
  Store.type.commonStore,
  Store.type.userStore,
)(
  observer((props: any) => {
    const commonStore = props[Store.type.commonStore] as CommonStore;
    const userStore = props[Store.type.userStore] as UserStore;

    const theme = useTheme();
    const navigate = useNavigate();

    const [uploading, setUploading] = useState(false);

    const [phoneNumber, setPhoneNumber] = useState("");
    const [documentsUpload, setDocumentsUpload] = useState<
      { file: File; document_id: string; level_id: string }[]
    >([]);

    useEffect(() => {
      const disposeAutorun = autorun(() => {
        setPhoneNumber(userStore.profile?.phone_number ?? "");
      });

      return () => disposeAutorun();
    }, [userStore]);

    const handleSubmit = async () => {
      setUploading(true);
      try {
        // validate each file size
        for (let i = 0; i < documentsUpload.length; i++) {
          if (documentsUpload[i].file.size > 2097152) {
            enqueueSnackbar(
              `Please select a file less than 2MB in size. (${allExtensionsString})`,
              {
                variant: "warning",
              },
            );
            setUploading(false);
            return;
          }
        }

        for (let i = 0; i < documentsUpload.length; i++) {
          const document = userStore.verificationDocuments?.find(
            (doc) => doc.documents_id === documentsUpload[i].document_id,
          );

          const file_key = await userStore.uploadDocumentFile({
            file: documentsUpload[i].file,
            type: documentsUpload[i].file.type,
          });
          if (document) {
            await userStore.updateFileVerificationDocument({
              verification_document_id: document.verification_document_id ?? "",
              file_name: documentsUpload[i].file.name,
              file_key,
            });
          } else {
            await userStore.createVerificationDocument({
              filename: documentsUpload[i].file.name,
              file_key,
              documents_id: documentsUpload[i].document_id,
              user_id: userStore.profile?.user_id ?? "",
              file_type: documentsUpload[i].file.type,
            });
          }
        }
        setDocumentsUpload([]);
        userStore.fetchVerificationDocuments();
        enqueueSnackbar("Documents uploaded successfully", {
          variant: "success",
        });
      } catch (error) {
        console.error(error);
        enqueueSnackbar("Error uploading documents", {
          variant: "error",
        });
      }
      setUploading(false);
    };
    useEffect(() => {
      commonStore.fetchAmlLevels();
      commonStore.fetchDocuments();
      userStore.fetchVerificationDocuments();
    }, [commonStore]);

    const handleChange = (
      value: File | null,
      document_id: string,
      level_id: string,
    ) => {
      //validate file size
      if (value && value.size > 2097152) {
        enqueueSnackbar(
          `Please select a file less than 2MB in size. (${allExtensionsString})`,
          {
            variant: "warning",
          },
        );
        return;
      }

      if (!value) {
        if (documentsUpload.find((f) => f.document_id === document_id)) {
          const newDocumentsUpload = documentsUpload.filter(
            (f) => f.document_id !== document_id,
          );
          setDocumentsUpload(newDocumentsUpload);
        }
        return;
      }
      if (!imageFileTypes.includes(value?.type || "")) {
        const newDocumentsUpload = documentsUpload.filter(
          (f) => f.document_id !== document_id,
        );
        setDocumentsUpload(newDocumentsUpload);
        enqueueSnackbar(
          `Please select an image file. (${allExtensionsString})`,
          {
            variant: "warning",
          },
        );
        return;
      }
      if (documentsUpload.find((f) => f.document_id === document_id)) {
        const newDocumentsUpload = documentsUpload.filter(
          (f) => f.document_id !== document_id,
        );
        setDocumentsUpload([
          ...newDocumentsUpload,
          { document_id, file: value, level_id },
        ]);
      } else {
        setDocumentsUpload([
          ...documentsUpload,
          { document_id, file: value, level_id },
        ]);
      }
    };
    return (
      <>
        <ContainerWithSidebar menu={profileSidebarMenuList}>
          <Container
            maxWidth="lg"
            sx={{
              py: "82px",
              [theme.breakpoints.up("lg")]: { maxWidth: "lg", py: "82px" },
            }}
          >
            <Stack spacing={2}>
              <Card
                sx={{
                  justifyContent: "center",
                  alignItems: "center",
                  display: "flex",
                }}
              >
                <Stack
                  spacing={4}
                  sx={{
                    width: 400,
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <Typography variant="h2" component="h1">
                    Phone Number
                  </Typography>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      width: "100%",
                    }}
                  >
                    <Typography variant="body1" component="p">
                      {phoneNumber}
                    </Typography>
                    <Typography
                      variant="body2"
                      component="p"
                      sx={{
                        alignItems: "center",
                        display: "flex",
                        gap: 1,
                      }}
                    >
                      {userStore.isPhoneVerified && (
                        <VerifiedRounded htmlColor="#2AD352" />
                      )}{" "}
                      {userStore.isPhoneVerified ? "Verified" : "Not Verified"}
                    </Typography>
                  </div>
                  {!userStore.isPhoneVerified && (
                    <Button
                      size="large"
                      disabled={userStore.isPhoneVerified}
                      variant="contained"
                      onClick={() => navigate("/profile/verify-phone-number")}
                    >
                      Verify
                    </Button>
                  )}
                </Stack>
              </Card>
              {commonStore.amlLevels
                .filter((f) =>
                  commonStore.documents.find((d) => d.aml_level_id === f.id),
                )
                .map((level, index) => (
                  <Card key={index}>
                    <Stack
                      spacing={4}
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <Typography variant="h2" component={"h2"}>
                        {level.name}
                      </Typography>
                      <Typography variant="body1" component={"p"}>
                        {level.description}
                      </Typography>
                      <Typography variant="body1" component={"p"}>
                        (All documents must be less than 2MB in size and in JPG,
                        PNG, or PDF format.)
                      </Typography>
                      {commonStore.documents
                        .filter((f) => f.aml_level_id === level.id)
                        .map((doc, index) => (
                          <Box key={index}>
                            <Grid
                              container
                              sx={{
                                width: "440px",
                              }}
                            >
                              <Grid
                                item
                                md={12}
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                }}
                              >
                                {userStore.verificationDocuments?.find(
                                  (f) =>
                                    f.documents_id === doc.id &&
                                    f.status !== "deleted",
                                ) ? (
                                  <ListItemText>{doc.name}</ListItemText>
                                ) : (
                                  <MuiFileInput
                                    value={
                                      documentsUpload.find(
                                        (f) => f.document_id === doc.id,
                                      )?.file || null
                                    }
                                    title={doc.name}
                                    label={doc.name}
                                    fullWidth
                                    placeholder="choose file"
                                    onChange={(value) => {
                                      handleChange(value, doc.id, level.id);
                                    }}
                                  />
                                )}
                                <Typography
                                  variant="body1"
                                  component={"p"}
                                  sx={{
                                    justifyContent: "center",
                                    display: "flex",
                                    gap: 1,
                                  }}
                                  display="flex"
                                  alignItems="center"
                                >
                                  {userStore.verificationDocuments?.find(
                                    (f) =>
                                      f.documents_id === doc.id &&
                                      f.status === "approved",
                                  ) && (
                                    <>
                                      <VerifiedRounded htmlColor="#2AD352" />{" "}
                                      Approved
                                    </>
                                  )}
                                  {userStore.verificationDocuments?.find(
                                    (f) =>
                                      f.documents_id === doc.id &&
                                      f.status === "waiting",
                                  ) && "Waiting"}
                                  {userStore.verificationDocuments?.find(
                                    (f) =>
                                      f.documents_id === doc.id &&
                                      f.status === "rejected",
                                  ) && "Rejected"}
                                  {userStore.verificationDocuments?.find(
                                    (f) =>
                                      f.documents_id === doc.id &&
                                      f.status === "rejected",
                                  ) && (
                                    <IconButton
                                      edge="end"
                                      color="primary"
                                      aria-label="delete"
                                      onClick={() => {
                                        userStore.setDeleteVerificationDocument(
                                          doc.id,
                                        );
                                      }}
                                    >
                                      <Delete />
                                    </IconButton>
                                  )}
                                </Typography>
                              </Grid>
                            </Grid>
                            {(() => {
                              const adminComment =
                                userStore.verificationDocuments?.find(
                                  (f) =>
                                    f.documents_id === doc.id &&
                                    f.status === "rejected",
                                )?.admin_comment;
                              if (adminComment)
                                return (
                                  <Typography
                                    variant="body2"
                                    color={theme.colors.errorFontColor}
                                    textAlign="end"
                                    width="440px"
                                    sx={{ wordWrap: "break-word" }}
                                  >
                                    {adminComment}
                                  </Typography>
                                );
                            })()}
                          </Box>
                        ))}
                      {commonStore.documents
                        .filter((f) => f.aml_level_id === level.id)
                        .find((doc) =>
                          userStore.verificationDocuments?.find(
                            (f) =>
                              f.documents_id === doc.id &&
                              f.status === "waiting",
                          ),
                        ) && (
                        <Typography
                          variant="body4"
                          textAlign="center"
                          width="440px"
                        >
                          Please note that the approval process may take up to
                          24 hours to complete. Your request will be reviewed
                          and processed within this timeframe. We appreciate
                          your patience and understanding.
                        </Typography>
                      )}
                      <Button
                        disabled={
                          documentsUpload.filter((f) => f.level_id === level.id)
                            .length === 0 || !userStore.isPhoneVerified
                        }
                        variant="outlined"
                        onClick={() => {
                          if (userStore.isPhoneVerified) {
                            handleSubmit();
                          } else {
                            enqueueSnackbar(
                              "Please verify phone number before verify account.",
                              { variant: "error" },
                            );
                          }
                        }}
                      >
                        Submit
                      </Button>
                    </Stack>
                  </Card>
                ))}
            </Stack>
          </Container>
        </ContainerWithSidebar>
        {/* Dialog waiting don't close utils state Uploaded is true */}
        <Dialog open={uploading}>
          <DialogTitle>Uploading</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Please wait while we are uploading your documents.
            </DialogContentText>
          </DialogContent>
        </Dialog>
      </>
    );
  }),
);

export default ProfileVerificationPage;
